import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { plainToInstance } from 'class-transformer';
import { Observable } from 'rxjs';
import { buildQueryString, HttpCallCache } from 'src/lib/utilities/api/http';
import {
	DefaultErrorMessage,
	mapResponse,
} from 'src/lib/utilities/api/map-response';
import { LearningPlanEditCreditsRequiredArgument } from './learing-plan-edit-credits-required.argument';
import {
	LearningPlanAlternateSectionArgument,
	LearningPlanAlternateSectionResponseModel,
} from './learning-plan-alternate-section.model';
import {
	LearningPlanEditSectionArgument,
	LearningPlanEditWeightArgument,
} from './learning-plan-edit-section.argument';
import { LearningPlanHistoryModel } from './learning-plan-history.model';
import { LearningPlanLockArgument } from './learning-plan-lock.argument';
import { LearningPlanRequirementOptionsModel } from './learning-plan-requirement-options.model';
import { LearningPlanScheduleSectionArgument } from './learning-plan-schedule-section.argument';
import {
	LearningPlanCourseSectionModel,
	LearningPlanModel,
} from './learning-plan.model';

@Injectable({
	providedIn: 'root',
})
export class LearningPlanService {
	constructor(private httpClient: HttpClient) {}

	public httpCallCache_getLearningPlan = new HttpCallCache<
		number,
		LearningPlanModel
	>(1000 * 60 * 30 /* 30 minutes */);

	public getLearningPlan = (
		uid: number,
		forceRefresh: boolean = false,
	): Observable<LearningPlanModel> => {
		if (forceRefresh) {
			this.httpCallCache_getLearningPlan.flushAll();
		}

		return this.httpCallCache_getLearningPlan.fetch$(
			uid,
			this.httpClient
				.get<{
					learning_plan: unknown;
				}>(`/api/v1/students/${uid}/learning_plan?_format=json`)
				.pipe(
					mapResponse(
						(r) => plainToInstance(LearningPlanModel, r.learning_plan),
						{
							errorCode: '61958CCD',
						},
					),
				),
		);
	};

	private httpCallCache_rebuildPlan = new HttpCallCache<
		number,
		LearningPlanModel
	>(1000);

	public rebuildPlan = (
		uid: number,
		forceRefresh: boolean = false,
	): Observable<LearningPlanModel> => {
		if (forceRefresh) {
			this.httpCallCache_getLearningPlan.flushAll();
		}

		return this.httpCallCache_rebuildPlan.fetch$(
			uid,
			this.httpClient
				.post<{
					learning_plan: unknown;
				}>(`/api/v1/students/${uid}/learning_plan/rebuild`, {})
				.pipe(
					mapResponse(
						(r) => plainToInstance(LearningPlanModel, r.learning_plan),
						{
							errorCode: '883600BF',
						},
					),
				),
		);
	};

	public downloadPDF = (uid: number): Observable<File> => {
		return this.httpClient
			.get<BlobPart>(`/api/v1/students/${uid}/learning_plan/download`, {
				headers: { Accept: 'application/pdf' },
				responseType: 'blob' as 'json',
			})
			.pipe(
				mapResponse(
					(r) =>
						new File([r], `learningPlan${new Date()}.pdf`, {
							type: 'application/pdf',
						}),
					{
						errorCode: '30FD9B5E',
					},
				),
			);
	};

	public viewAvailableRequirements = (
		uid: number,
	): Observable<LearningPlanRequirementOptionsModel> => {
		return this.httpClient
			.get<unknown>(
				`/api/v1/students/${uid}/learning_plan/available_requirements`,
			)
			.pipe(
				mapResponse(
					(r) => plainToInstance(LearningPlanRequirementOptionsModel, r),
					{
						errorCode: '61958CCD',
					},
				),
			);
	};

	public viewAlternativeSections = (
		uid: number,
		args: Partial<LearningPlanAlternateSectionArgument>,
	): Observable<LearningPlanAlternateSectionResponseModel> => {
		const query = buildQueryString(args);

		return this.httpClient
			.get<unknown>(
				`/api/v1/students/${uid}/learning_plan/alternative_sections${query}`,
			)
			.pipe(
				mapResponse(
					(r) => plainToInstance(LearningPlanAlternateSectionResponseModel, r),
					{
						errorCode: '0145A373',
					},
				),
			);
	};

	public viewHistoricalActions = (
		uid: number,
		start_date: string,
		end_date: string,
	): Observable<LearningPlanHistoryModel[]> => {
		let qs = null;
		if (start_date && end_date) {
			qs = buildQueryString({ start_date, end_date });
		}
		return this.httpClient
			.get<{
				historical_actions: unknown[];
			}>(
				`/api/v1/students/${uid}/learning_plan/historical_actions${qs != null ? qs : ''}`,
			)
			.pipe(
				mapResponse(
					(r) =>
						plainToInstance(LearningPlanHistoryModel, r.historical_actions),
					{
						errorCode: '350BAED5',
					},
				),
			);
	};

	public setLock = (
		uid: number,
		args: LearningPlanLockArgument,
	): Observable<{
		planned_only_registration: boolean;
		locked: boolean;
	}> => {
		return this.httpClient
			.post<{
				planned_only_registration: boolean;
				locked: boolean;
			}>(`/api/v1/students/${uid}/learning_plan/lock`, args)
			.pipe(
				mapResponse((r) => r, {
					errorCode: '88FECF94',
				}),
			);
	};

	public editCreditsRequired = (
		uid: number,
		args: LearningPlanEditCreditsRequiredArgument,
	): Observable<LearningPlanModel> => {
		return this.httpClient
			.post<{
				learning_plan: unknown;
			}>(`/api/v1/students/${uid}/learning_plan/credits`, args)
			.pipe(
				mapResponse(
					(r) => plainToInstance(LearningPlanModel, r.learning_plan),
					{
						errorCode: 'C07717CA',
					},
				),
			);
	};

	public editSection = (
		uid: number,
		sectionId: number,
		args: Partial<LearningPlanEditSectionArgument>,
	): Observable<unknown> => {
		return this.httpClient
			.post<unknown>(
				`/api/v1/students/${uid}/learning_plan/section/${sectionId}`,
				args,
			)
			.pipe(
				mapResponse((r) => r, {
					errorCode: 'C07717CA',
				}),
			);
	};

	public addSection = (
		uid: number,
		args: Partial<LearningPlanEditSectionArgument>,
	): Observable<LearningPlanCourseSectionModel> => {
		return this.httpClient
			.post<{
				upcoming_course: unknown;
			}>(`/api/v1/students/${uid}/learning_plan/section`, args)
			.pipe(
				mapResponse(
					(r) =>
						plainToInstance(LearningPlanCourseSectionModel, r.upcoming_course),
					{
						errorCode: '61C2A5E9',
					},
				),
			);
	};

	public deleteSection = (
		uid: number,
		sectionId: number,
	): Observable<unknown> => {
		return this.httpClient
			.delete<unknown>(
				`/api/v1/students/${uid}/learning_plan/section/${sectionId}`,
			)
			.pipe(
				mapResponse((r) => r, {
					errorCode: '613B4BBF',
				}),
			);
	};

	public deletePersistentSuggestion = (
		uid: number,
		sectionId: number,
	): Observable<unknown> => {
		return this.httpClient
			.delete<unknown>(
				`/api/v1/students/${uid}/learning_plan/section/${sectionId}/remove_persistent_suggestion`,
			)
			.pipe(
				mapResponse((r) => r, {
					errorCode: '74EF39AE',
				}),
			);
	};

	public schedulePlannedSection = (
		uid: number,
		sectionId: number,
		args: LearningPlanScheduleSectionArgument,
	): Observable<LearningPlanCourseSectionModel> => {
		return this.httpClient
			.post<{
				upcoming_course: unknown;
			}>(
				`/api/v1/students/${uid}/learning_plan/section/${sectionId}/schedule`,
				args,
			)
			.pipe(
				mapResponse(
					(r) =>
						plainToInstance(LearningPlanCourseSectionModel, r.upcoming_course),
					{
						errorCode: 'DB8BAE26',
					},
				),
			);
	};

	public markCourseExempt = (
		uid: number,
		courseCode: string,
		exempt: boolean,
	): Observable<LearningPlanModel> => {
		return this.httpClient
			.post<{ learning_plan: unknown }>(
				`/api/v1/students/${uid}/learning_plan/exempt`,
				{
					course_requirements: [{ course_code: courseCode, exempt }],
				},
			)
			.pipe(
				mapResponse(
					(r) => plainToInstance(LearningPlanModel, r.learning_plan),
					{
						errorCode: '4BFD7B17',
					},
				),
			);
	};

	public editWeights = (
		uid: number,
		planned: LearningPlanEditWeightArgument[],
	): Observable<unknown> => {
		return this.httpClient
			.post<unknown>(`/api/v1/students/${uid}/learning_plan/edit_weights`, {
				planned,
			})
			.pipe(
				mapResponse((r) => r, {
					errorCode: 'F69A3680',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	public deleteAllPersistentSuggestions = (
		uid: number,
	): Observable<unknown> => {
		return this.httpClient
			.delete<unknown>(
				`/api/v1/students/${uid}/learning_plan/remove_all_persistent_suggestions`,
			)
			.pipe(
				mapResponse((r) => r, {
					errorCode: 'F1FD45D4',
				}),
			);
	};
}
